import { validateHttpsUrl, validateRequired } from '@workspace/seedforge-shared'
import { Badge } from '@workspace/ui/components/badge'
import { Checkbox } from '@workspace/ui/components/checkbox'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { Textarea } from '@workspace/ui/components/textarea'
import { cn } from '@workspace/ui/lib/utils'
import { AlertTriangle, Check, FileText, Loader2, Pencil, Upload, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { FieldLabel } from '../components/field-label'

// --- Types ---

export type PersonaStatus = 'ready' | 'processing' | 'failed' | 'none'

export interface EnumOption {
  value: string
  label: string
}

export type InvestmentStage = 'PRE_SEED' | 'SEED'

export type TicketCurrency = 'USD' | 'EUR' | 'GBP'

const CURRENCIES: { value: TicketCurrency; symbol: string; label: string }[] = [
  { value: 'USD', symbol: '$', label: 'USD ($)' },
  { value: 'EUR', symbol: '€', label: 'EUR (€)' },
  { value: 'GBP', symbol: '£', label: 'GBP (£)' },
]

const STAGE_OPTIONS: EnumOption[] = [
  { value: 'PRE_SEED', label: 'Pre-seed' },
  { value: 'SEED', label: 'Seed' },
]

export interface InvestorPersonData {
  name: string
  /** Empty means no title. */
  title: string
}

export interface InvestorFirmData {
  fundName: string
  fundDescription: string
  websiteUrl: string
}

export interface InvestorFocusData {
  investmentAreas: string[]
  investmentGeographies: string[]
  investmentStages: InvestmentStage[]
  minTicketSize: number | null
  maxTicketSize: number | null
  ticketCurrency: TicketCurrency
  /** Words and phrases that matter to the investor (the watchdog's criteria). */
  focusKeywords: string[]
  killCriteria: string
}

export interface PersonaData {
  personaStatus: PersonaStatus
  personaError?: string | null
  personaBrief?: string | null
}

export interface InvestorProfileProps {
  /** The signed-in person: what a founder sees when this investor follows their startup */
  personName: string
  /** Their title at the firm, shown to founders between their name and the firm */
  personTitle?: string | null
  /** Firm name, shown to founders next to the person's name */
  fundName: string
  fundDescription?: string
  websiteUrl?: string | null
  investmentAreas?: string[]
  investmentGeographies?: string[]
  investmentStages?: InvestmentStage[]
  minTicketSize?: number | null
  maxTicketSize?: number | null
  ticketCurrency?: TicketCurrency
  focusKeywords?: string[]
  /** What the watchdog skips */
  killCriteria?: string | null
  areaOptions?: EnumOption[]
  regionOptions?: EnumOption[]
  persona?: PersonaData | null
  /** Current thesis document file name, if uploaded */
  thesisFileName?: string | null
  /** Whether the cards can be edited (read-only for team members the owner manages) */
  editable?: boolean
  /** Whether the focus card can be edited; it is the person's own focus, so a member edits it too. Defaults to `editable`. */
  focusEditable?: boolean
  onPersonSave?: (data: InvestorPersonData) => void
  onFirmSave?: (data: InvestorFirmData) => void
  onFocusSave?: (data: InvestorFocusData) => void
  /** Open the focus card for editing and bring it into view (the watchdog's Edit focus). */
  editFocus?: boolean
  onThesisUpload?: (file: File) => void | Promise<void>
  onThesisRemove?: () => void
}

// --- Helpers ---

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function hostname(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

function formatAmount(value: number, symbol: string): string {
  if (value >= 1_000_000) return `${symbol}${value / 1_000_000}M`
  if (value >= 1_000) return `${symbol}${value / 1_000}k`
  return `${symbol}${value}`
}

function formatTicket(
  min: number | null,
  max: number | null,
  currency: TicketCurrency
): string | null {
  if (min === null && max === null) return null
  const symbol = CURRENCIES.find((c) => c.value === currency)?.symbol ?? '$'
  if (min !== null && max !== null) {
    return `${formatAmount(min, symbol)} to ${formatAmount(max, symbol)}`
  }
  return min !== null
    ? `From ${formatAmount(min, symbol)}`
    : `Up to ${formatAmount(max ?? 0, symbol)}`
}

/** "recurring revenue, B2B" becomes ["recurring revenue", "B2B"]; blanks and repeats are dropped. */
function parseKeywords(text: string): string[] {
  const words = text
    .split(/[,\n]/)
    .map((w) => w.trim())
    .filter(Boolean)
  return [...new Set(words)]
}

function labelOf(options: EnumOption[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
}

// --- Shared card pieces ---

function Card({
  title,
  description,
  onEdit,
  children,
}: {
  title: string
  description: string
  onEdit?: () => void
  children: ReactNode
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        {onEdit && (
          <SecondaryButton size="sm" icon={<Pencil className="size-3.5" />} onClick={onEdit}>
            Edit
          </SecondaryButton>
        )}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-6 border-b border-border py-2.5 text-sm last:border-b-0 last:pb-0 first:pt-0">
      <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-foreground">
        {children}
      </div>
    </div>
  )
}

function Chips({ values, empty = 'Not set' }: { values: string[]; empty?: string }) {
  if (values.length === 0) return <span className="text-muted-foreground">{empty}</span>
  return (
    <>
      {values.map((v) => (
        <Badge key={v} variant="secondary" size="md">
          {v}
        </Badge>
      ))}
    </>
  )
}

function EditorButtons({ onCancel, onSave }: { onCancel: () => void; onSave: () => void }) {
  return (
    <div className="flex justify-end gap-2 pt-1">
      <SecondaryButton size="sm" icon={<X className="size-3.5" />} onClick={onCancel}>
        Cancel
      </SecondaryButton>
      <CtaButton size="sm" icon={<Check className="size-3.5" />} onClick={onSave}>
        Save
      </CtaButton>
    </div>
  )
}

function OptionGrid({
  options,
  selected,
  onToggle,
}: {
  options: EnumOption[]
  selected: string[]
  onToggle: (value: string) => void
}) {
  const groupId = useId()
  return (
    <div className="mt-1 grid grid-cols-2 gap-2 md:grid-cols-3">
      {options.map((option) => (
        <label
          key={option.value}
          htmlFor={`${groupId}-${option.value}`}
          className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card/50 px-3 py-2 transition-colors hover:bg-accent/50"
        >
          <Checkbox
            id={`${groupId}-${option.value}`}
            checked={selected.includes(option.value)}
            onCheckedChange={() => onToggle(option.value)}
          />
          <span className="text-xs">{option.label}</span>
        </label>
      ))}
    </div>
  )
}

// --- Cards ---

function PersonEditor({
  name,
  title,
  onSave,
  onCancel,
}: InvestorPersonData & { onSave: (data: InvestorPersonData) => void; onCancel: () => void }) {
  const [nameDraft, setNameDraft] = useState(name)
  const [titleDraft, setTitleDraft] = useState(title)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Name</FieldLabel>
        <Input
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          className={cn('mt-1', error && 'ring-destructive/50 ring-[1.5px]')}
        />
        {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
      </div>
      <div>
        <FieldLabel>Title, optional</FieldLabel>
        <Input
          value={titleDraft}
          onChange={(e) => setTitleDraft(e.target.value)}
          placeholder="Partner"
          className="mt-1"
        />
      </div>
      <EditorButtons
        onCancel={onCancel}
        onSave={() => {
          const problem = validateRequired(nameDraft, 'Name')
          setError(problem)
          if (!problem) onSave({ name: nameDraft.trim(), title: titleDraft.trim() })
        }}
      />
    </div>
  )
}

function FirmEditor({
  fundName,
  fundDescription,
  websiteUrl,
  onSave,
  onCancel,
}: InvestorFirmData & { onSave: (data: InvestorFirmData) => void; onCancel: () => void }) {
  const [name, setName] = useState(fundName)
  const [description, setDescription] = useState(fundDescription)
  const [website, setWebsite] = useState(websiteUrl)
  const [errors, setErrors] = useState<{ name?: string | null; website?: string | null }>({})

  function handleSave() {
    const next = {
      name: validateRequired(name, 'Firm name'),
      website: validateHttpsUrl(website),
    }
    setErrors(next)
    if (next.name || next.website) return
    onSave({ fundName: name, fundDescription: description, websiteUrl: website })
  }

  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Firm name</FieldLabel>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={cn('mt-1', errors.name && 'ring-destructive/50 ring-[1.5px]')}
        />
        {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
      </div>
      <div>
        <FieldLabel>Firm website</FieldLabel>
        <Input
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://example.com"
          className={cn('mt-1', errors.website && 'ring-destructive/50 ring-[1.5px]')}
        />
        {errors.website && <p className="mt-1 text-xs text-red-400">{errors.website}</p>}
      </div>
      <div>
        <FieldLabel>Description</FieldLabel>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="mt-1"
        />
      </div>
      <EditorButtons onCancel={onCancel} onSave={handleSave} />
    </div>
  )
}

function parseAmount(value: string): number | null {
  const digits = value.replace(/[^0-9]/g, '')
  return digits === '' ? null : Number(digits)
}

function FocusEditor({
  focus,
  areaOptions,
  regionOptions,
  onSave,
  onCancel,
}: {
  focus: InvestorFocusData
  areaOptions: EnumOption[]
  regionOptions: EnumOption[]
  onSave: (data: InvestorFocusData) => void
  onCancel: () => void
}) {
  const [stages, setStages] = useState<string[]>(focus.investmentStages)
  const [areas, setAreas] = useState(focus.investmentAreas)
  const [regions, setRegions] = useState(focus.investmentGeographies)
  const [min, setMin] = useState(focus.minTicketSize?.toString() ?? '')
  const [max, setMax] = useState(focus.maxTicketSize?.toString() ?? '')
  const [currency, setCurrency] = useState<TicketCurrency>(focus.ticketCurrency)
  const [keywords, setKeywords] = useState(focus.focusKeywords.join(', '))
  const [killCriteria, setKillCriteria] = useState(focus.killCriteria)

  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Stages</FieldLabel>
        <OptionGrid
          options={STAGE_OPTIONS}
          selected={stages}
          onToggle={(v) => setStages((prev) => toggle(prev, v))}
        />
      </div>
      <div>
        <FieldLabel>Sectors</FieldLabel>
        <OptionGrid
          options={areaOptions}
          selected={areas}
          onToggle={(v) => setAreas((prev) => toggle(prev, v))}
        />
      </div>
      <div>
        <FieldLabel>Regions</FieldLabel>
        <OptionGrid
          options={regionOptions}
          selected={regions}
          onToggle={(v) => setRegions((prev) => toggle(prev, v))}
        />
      </div>
      <div>
        <FieldLabel>Ticket size</FieldLabel>
        <p className="mt-1 text-xs text-muted-foreground">What you invest in one startup.</p>
        <div className="mt-2 grid grid-cols-3 gap-3">
          <div>
            <FieldLabel>Currency</FieldLabel>
            <Select value={currency} onValueChange={(v) => setCurrency(v as TicketCurrency)}>
              <SelectTrigger className="mt-1 w-full" aria-label="Currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <FieldLabel>Minimum</FieldLabel>
            <Input
              inputMode="numeric"
              aria-label="Minimum"
              value={min}
              onChange={(e) => setMin(e.target.value)}
              placeholder="100000"
              className="mt-1"
            />
          </div>
          <div>
            <FieldLabel>Maximum</FieldLabel>
            <Input
              inputMode="numeric"
              aria-label="Maximum"
              value={max}
              onChange={(e) => setMax(e.target.value)}
              placeholder="500000"
              className="mt-1"
            />
          </div>
        </div>
      </div>
      <div>
        <FieldLabel>Words that matter</FieldLabel>
        <Input
          aria-label="Words that matter"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="recurring revenue, B2B"
          className="mt-1"
        />
        <p className="mt-1 text-xs text-muted-foreground">Separate words or phrases with commas.</p>
      </div>
      <div>
        <FieldLabel>Leave out</FieldLabel>
        <Textarea
          value={killCriteria}
          onChange={(e) => setKillCriteria(e.target.value)}
          rows={2}
          placeholder="Your watchdog skips startups in these areas."
          className="mt-1"
        />
      </div>
      <EditorButtons
        onCancel={onCancel}
        onSave={() =>
          onSave({
            investmentStages: stages as InvestmentStage[],
            investmentAreas: areas,
            investmentGeographies: regions,
            minTicketSize: parseAmount(min),
            maxTicketSize: parseAmount(max),
            ticketCurrency: currency,
            focusKeywords: parseKeywords(keywords),
            killCriteria,
          })
        }
      />
    </div>
  )
}

function ThesisDropzone({
  onDrop,
  onCancel,
  busy = false,
  error = null,
}: {
  onDrop: (file: File) => void
  onCancel: () => void
  /** A file is being uploaded: the box says so and ignores further drops. */
  busy?: boolean
  error?: string | null
}) {
  const handleDrop = useCallback(
    (accepted: File[]) => {
      const file = accepted[0]
      if (file) onDrop(file)
    },
    [onDrop]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024,
    onDropAccepted: handleDrop,
    disabled: busy,
  })

  return (
    <div className="space-y-3">
      <div
        {...getRootProps()}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-6 py-6 text-center transition-all',
          isDragActive
            ? 'border-[#f97316] bg-[#f97316]/5'
            : 'border-border/60 hover:border-[#f97316]/40 hover:bg-card/30'
        )}
      >
        <input {...getInputProps()} />
        {busy ? (
          <Loader2 className="size-5 animate-spin text-muted-foreground/70" />
        ) : (
          <Upload className="size-5 text-muted-foreground/50" />
        )}
        <p className="text-xs text-muted-foreground">
          {busy
            ? 'Uploading your thesis…'
            : isDragActive
              ? 'Drop your thesis here'
              : 'Drag & drop or click to upload'}
        </p>
        <p className="text-[10px] text-muted-foreground/40">PDF, DOC, DOCX or TXT up to 50 MB</p>
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <SecondaryButton size="sm" icon={<X className="size-3.5" />} onClick={onCancel}>
          Cancel
        </SecondaryButton>
      </div>
    </div>
  )
}

/**
 * The thesis upload with its outcome: it stays open and says "Uploading…" while the file goes, closes
 * when it is saved, and keeps the file's error on screen when it is not (a failure used to vanish).
 */
function ThesisUploadBox({
  onUpload,
  onClose,
}: {
  onUpload: (file: File) => void | Promise<void>
  onClose: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function upload(file: File) {
    setBusy(true)
    setError(null)
    try {
      await onUpload(file)
      onClose()
    } catch {
      setError('We could not upload your thesis. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  return <ThesisDropzone onDrop={upload} onCancel={onClose} busy={busy} error={error} />
}

/** Where a persona stands, shown only while there is something to do or wait for. */
function PersonaNotice({
  status,
  onUploadThesis,
}: {
  status: PersonaStatus
  onUploadThesis?: (file: File) => void | Promise<void>
}) {
  // The upload opens here, in the notice, so nobody has to scroll to the thesis card to use it.
  const [uploading, setUploading] = useState(false)
  // Nothing to show until a persona exists or a thesis is being read; the thesis card offers the upload.
  if (status === 'ready' || status === 'none') return null
  const content = {
    processing: {
      icon: <Loader2 className="size-4 animate-spin text-amber-500" />,
      title: 'Building your investor persona',
      text: "We're analyzing your investment profile to create your personalized investor persona. This usually takes a few minutes.",
    },
    failed: {
      icon: <AlertTriangle className="size-4 text-red-500" />,
      title: 'We could not build your persona',
      text: 'We could not build your persona from the information we found. Upload your investment thesis and we will build it from that instead.',
    },
  }[status]

  return (
    <div className="rounded-xl border border-border bg-muted/30 px-5 py-4">
      <div className="flex items-center gap-2.5">
        {content.icon}
        <h3 className="text-sm font-semibold text-foreground">{content.title}</h3>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{content.text}</p>
      {status !== 'processing' && onUploadThesis && !uploading && (
        <CtaButton size="sm" className="mt-3" onClick={() => setUploading(true)}>
          Upload your thesis
        </CtaButton>
      )}
      {uploading && onUploadThesis && (
        <div className="mt-3">
          <ThesisUploadBox onUpload={onUploadThesis} onClose={() => setUploading(false)} />
        </div>
      )}
    </div>
  )
}

// --- Main component ---

type Editing = 'person' | 'firm' | 'focus' | 'thesis' | null

export function InvestorProfile({
  personName,
  personTitle,
  fundName,
  fundDescription,
  websiteUrl,
  investmentAreas = [],
  investmentGeographies = [],
  investmentStages = [],
  minTicketSize = null,
  maxTicketSize = null,
  ticketCurrency = 'USD',
  focusKeywords = [],
  killCriteria,
  editFocus = false,
  areaOptions = [],
  regionOptions = [],
  persona,
  thesisFileName,
  editable = false,
  focusEditable = editable,
  onPersonSave,
  onFirmSave,
  onFocusSave,
  onThesisUpload,
  onThesisRemove,
}: InvestorProfileProps) {
  const [editing, setEditing] = useState<Editing>(null)
  // Edit focus from the watchdog opens this card for editing and brings it into view. The editor opens once the
  // profile is editable (its props can arrive after the first render), and the scroll waits a frame, past the
  // router's own scroll reset after navigation.
  const focusRef = useRef<HTMLDivElement>(null)
  const openedFromLink = useRef(false)
  useEffect(() => {
    if (!editFocus || openedFromLink.current || !focusEditable || !onFocusSave) return
    openedFromLink.current = true
    setEditing('focus')
  }, [editFocus, focusEditable, onFocusSave])
  useEffect(() => {
    if (!editFocus) return
    const frame = requestAnimationFrame(() => focusRef.current?.scrollIntoView({ block: 'start' }))
    return () => cancelAnimationFrame(frame)
  }, [editFocus])
  const personaStatus = persona?.personaStatus ?? 'none'
  const ticket = formatTicket(minTicketSize, maxTicketSize, ticketCurrency)
  const canEdit = (card: Exclude<Editing, null>, handler?: unknown) =>
    (card === 'focus' ? focusEditable : editable) && handler ? () => setEditing(card) : undefined

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-foreground">
          Your investor profile
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          How you appear to founders, and what SeedForge watches for you.
        </p>
      </div>

      <PersonaNotice status={personaStatus} onUploadThesis={onThesisUpload} />

      <Card
        title="What founders see"
        description="When you follow a startup, the founder sees that you follow."
        onEdit={editing === null ? canEdit('person', onPersonSave) : undefined}
      >
        {editing === 'person' ? (
          <PersonEditor
            name={personName}
            title={personTitle ?? ''}
            onSave={(data) => {
              onPersonSave?.(data)
              setEditing(null)
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <div>
            <div className="flex items-center gap-3 rounded-lg bg-background/60 px-4 py-3">
              <span className="flex size-10 items-center justify-center rounded-lg bg-muted text-sm font-medium text-foreground">
                {getInitials(personName)}
              </span>
              <div>
                <p className="text-sm font-medium text-foreground">{personName}</p>
                <p className="text-xs text-muted-foreground">
                  {personTitle ? `${personTitle} · ${fundName}` : 'Add your title'}
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card
        title="Your firm"
        description="We use these details to confirm your investor account."
        onEdit={editing === null ? canEdit('firm', onFirmSave) : undefined}
      >
        {editing === 'firm' ? (
          <FirmEditor
            fundName={fundName}
            fundDescription={fundDescription ?? ''}
            websiteUrl={websiteUrl ?? ''}
            onSave={(data) => {
              onFirmSave?.(data)
              setEditing(null)
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <div>
            <Row label="Firm name">{fundName}</Row>
            <Row label="Firm website">
              {websiteUrl ? (
                hostname(websiteUrl)
              ) : (
                <span className="text-muted-foreground">No website added</span>
              )}
            </Row>
          </div>
        )}
      </Card>

      <div ref={focusRef}>
        <Card
          title="Your focus"
          description="Your Discovery watchdog and search suggestions start from this."
          onEdit={editing === null ? canEdit('focus', onFocusSave) : undefined}
        >
          {editing === 'focus' ? (
            <FocusEditor
              focus={{
                investmentAreas,
                investmentGeographies,
                investmentStages,
                minTicketSize,
                maxTicketSize,
                ticketCurrency,
                focusKeywords,
                killCriteria: killCriteria ?? '',
              }}
              areaOptions={areaOptions}
              regionOptions={regionOptions}
              onSave={(data) => {
                onFocusSave?.(data)
                setEditing(null)
              }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div>
              <Row label="Stages">
                <Chips values={investmentStages.map((s) => labelOf(STAGE_OPTIONS, s))} />
              </Row>
              <Row label="Sectors">
                <Chips values={investmentAreas.map((a) => labelOf(areaOptions, a))} />
              </Row>
              <Row label="Regions">
                <Chips values={investmentGeographies.map((g) => labelOf(regionOptions, g))} />
              </Row>
              <Row label="Ticket size">
                <Chips values={ticket ? [ticket] : []} />
              </Row>
              <Row label="Words that matter">
                <Chips values={focusKeywords} />
              </Row>
              <Row label="Leave out">
                <Chips
                  values={(killCriteria ?? '')
                    .split(/[,\n]/)
                    .map((v) => v.trim())
                    .filter(Boolean)}
                  empty="Nothing left out"
                />
              </Row>
            </div>
          )}
        </Card>
      </div>

      <Card
        title="Your thesis"
        description="We read it to suggest your focus."
        onEdit={editing === null && !thesisFileName ? canEdit('thesis', onThesisUpload) : undefined}
      >
        {editing === 'thesis' ? (
          <ThesisUploadBox
            onUpload={(file) => onThesisUpload?.(file)}
            onClose={() => setEditing(null)}
          />
        ) : thesisFileName ? (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <FileText className="size-4 text-muted-foreground" />
              <span className="text-sm text-foreground">{thesisFileName}</span>
            </div>
            {editable && (
              <div className="flex gap-2">
                {onThesisUpload && (
                  <SecondaryButton size="sm" onClick={() => setEditing('thesis')}>
                    Replace
                  </SecondaryButton>
                )}
                {onThesisRemove && (
                  <SecondaryButton size="sm" tone="danger" onClick={onThesisRemove}>
                    Remove
                  </SecondaryButton>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No thesis uploaded yet</p>
        )}
      </Card>
    </div>
  )
}
